// ===========================================
// Enrollment Analytics Page
// ===========================================
// Complete analytics dashboard for student enrollments
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React, { useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { TrendingUp } from '@mui/icons-material';
import PageHeader from '../../../components/common/PageHeader';
import {
  EnrollmentFilters,
  EnrollmentSummaryCards,
  EnrollmentTrendChart,
  RetentionChart,
  ProgramLevelDistChart,
  EnrollmentByTypeChart,
} from '../../../components/reports';
import {
  useEnrollmentTrends,
  useRetentionRates,
  useProgramLevelDistribution,
  useEnrollmentByLessonType,
} from '../../../hooks/useReports';
import { EnrollmentReportFilters as FilterType } from '../../../api/reports.api';

const EnrollmentAnalyticsPage: React.FC = () => {
  const [filters, setFilters] = useState<FilterType>({});
  const [trendMonths] = useState(12);

  // Fetch data
  const { data: trendsData, isLoading: trendsLoading } = useEnrollmentTrends(trendMonths);
  const { data: retentionData, isLoading: retentionLoading } = useRetentionRates(filters);
  const { data: programLevelData, isLoading: programLevelLoading } =
    useProgramLevelDistribution();
  const { data: byTypeData, isLoading: byTypeLoading } = useEnrollmentByLessonType(filters);

  return (
    <Box>
      <PageHeader
        title="Enrolment Analytics"
        subtitle="Track student enrolment trends, retention rates, and distribution"
        icon={<TrendingUp />}
      />

      {/* Filters */}
      <EnrollmentFilters filters={filters} onChange={setFilters} />

      {/* Summary Cards */}
      <EnrollmentSummaryCards summary={trendsData?.summary} isLoading={trendsLoading} />

      {/* Enrollment Trend Chart - Full Width */}
      <EnrollmentTrendChart
        data={trendsData?.monthlyTrends}
        isLoading={trendsLoading}
      />

      {/* Grid Layout: Retention Chart + Program Level Distribution */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <RetentionChart data={retentionData?.byLesson} isLoading={retentionLoading} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProgramLevelDistChart
            data={programLevelData?.byLevel}
            isLoading={programLevelLoading}
          />
        </Grid>
      </Grid>

      {/* Enrollment by Type Chart - Highlight HYBRID */}
      <EnrollmentByTypeChart data={byTypeData?.byType} isLoading={byTypeLoading} />

      {/* Export Placeholder */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Export functionality coming soon (CSV/PDF)
        </Typography>
      </Box>
    </Box>
  );
};

export default EnrollmentAnalyticsPage;
